import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
} from "@mui/material";
import {
    Search as SearchIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    FolderSpecial as ProjectIcon,
    Description as QuoteIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CompanyService from "../services/Company.service";
import ContactService from "../services/Contact.service";
import ProjectService from "../services/Project.service";
import QuoteService from "../services/Quote.service";

function ResultCard({ onClick, primary, secondary, chips }) {
    return (
        <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
            <CardActionArea onClick={onClick}>
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} noWrap>
                                {primary}
                            </Typography>
                            {secondary && (
                                <Typography variant="caption" color="text.secondary" noWrap component="div">
                                    {secondary}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                            {chips}
                            <ChevronRightIcon fontSize="small" color="action" />
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

// Search-first list: types into a field, results come from the server, click drills into detail.
// initialQuery/onQueryChange keep the search term mirrored into the URL so a browser
// back-navigation from a detail page restores this tab's search instead of a blank one.
function SearchTab({ searchFn, placeholder, noun, renderItem, initialQuery = "", onQueryChange }) {
    const [search, setSearch] = useState(initialQuery);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasSearched, setHasSearched] = useState(Boolean(initialQuery.trim()));
    const debounceRef = useRef(null);

    const runSearch = useCallback(
        (term) => {
            setLoading(true);
            setError("");
            searchFn(term)
                .then((res) => setItems(res.data?.results ?? res.data ?? []))
                .catch(() => setError(`Failed to load ${noun}.`))
                .finally(() => setLoading(false));
        },
        [searchFn, noun],
    );

    // Restore results for a search term carried over from the URL (e.g. after a back-navigation).
    useEffect(() => {
        if (initialQuery.trim()) runSearch(initialQuery.trim());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e) => {
        const val = e.target.value;
        setSearch(val);
        clearTimeout(debounceRef.current);
        if (!val.trim()) {
            setItems([]);
            setHasSearched(false);
            onQueryChange?.("");
            return;
        }
        setHasSearched(true);
        debounceRef.current = setTimeout(() => {
            runSearch(val.trim());
            onQueryChange?.(val.trim());
        }, 350);
    };

    return (
        <Box>
            <TextField
                size="small"
                fullWidth
                autoFocus
                placeholder={placeholder}
                value={search}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: loading ? (
                        <InputAdornment position="end">
                            <CircularProgress size={16} />
                        </InputAdornment>
                    ) : null,
                }}
            />

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!hasSearched ? (
                <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
                    <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                    <Typography variant="body2">Start typing to search {noun}</Typography>
                </Box>
            ) : items.length === 0 && !loading ? (
                <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
                    <Typography variant="body2">No {noun} match your search.</Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {items.map((item) => renderItem(item))}
                </Box>
            )}
        </Box>
    );
}

const TAB_KEYS = ["companies", "contacts", "projects", "quotes"];

function CRM() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => {
        const idx = TAB_KEYS.indexOf(searchParams.get("tab"));
        return idx === -1 ? 0 : idx;
    });
    // Captured once on mount — only relevant to whichever tab is initially active.
    const initialQueryRef = useRef(searchParams.get("q") || "");

    const handleTabChange = (_, newIndex) => {
        setTab(newIndex);
        setSearchParams({ tab: TAB_KEYS[newIndex] }, { replace: true });
    };

    const handleQueryChange = (term) => {
        const params = { tab: TAB_KEYS[tab] };
        if (term) params.q = term;
        setSearchParams(params, { replace: true });
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "Montserrat, sans-serif" }}>
                    CRM
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Look up companies, contacts, projects, and quotes
                </Typography>
            </Box>

            <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
                variant="fullWidth"
            >
                <Tab
                    icon={<BusinessIcon fontSize="small" />}
                    label="Companies"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none", fontSize: "0.7rem", minHeight: 56, px: 0.5 }}
                />
                <Tab
                    icon={<PersonIcon fontSize="small" />}
                    label="Contacts"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none", fontSize: "0.7rem", minHeight: 56, px: 0.5 }}
                />
                <Tab
                    icon={<ProjectIcon fontSize="small" />}
                    label="Projects"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none", fontSize: "0.7rem", minHeight: 56, px: 0.5 }}
                />
                <Tab
                    icon={<QuoteIcon fontSize="small" />}
                    label="Quotes"
                    sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, textTransform: "none", fontSize: "0.7rem", minHeight: 56, px: 0.5 }}
                />
            </Tabs>

            {tab === 0 && (
                <SearchTab
                    noun="companies"
                    placeholder="Search companies…"
                    searchFn={(term) => CompanyService.search(term)}
                    initialQuery={tab === 0 ? initialQueryRef.current : ""}
                    onQueryChange={handleQueryChange}
                    renderItem={(company) => (
                        <ResultCard
                            key={company.id}
                            onClick={() => navigate(`/crm/companies/${company.id}`)}
                            primary={company.name}
                            secondary={company.website}
                        />
                    )}
                />
            )}

            {tab === 1 && (
                <SearchTab
                    noun="contacts"
                    placeholder="Search contacts by name or email…"
                    searchFn={(term) => ContactService.search(term)}
                    initialQuery={tab === 1 ? initialQueryRef.current : ""}
                    onQueryChange={handleQueryChange}
                    renderItem={(contact) => (
                        <ResultCard
                            key={contact.id}
                            onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                            primary={contact.name}
                            secondary={[contact.job_title, contact.company?.name].filter(Boolean).join(" · ") || contact.email}
                            chips={contact.is_archived ? <Chip label="Archived" size="small" sx={{ mr: 0.5 }} /> : null}
                        />
                    )}
                />
            )}

            {tab === 2 && (
                <SearchTab
                    noun="projects"
                    placeholder="Search projects by name, number, or customer…"
                    searchFn={(term) => ProjectService.search(term)}
                    initialQuery={tab === 2 ? initialQueryRef.current : ""}
                    onQueryChange={handleQueryChange}
                    renderItem={(project) => (
                        <ResultCard
                            key={project.id}
                            onClick={() => navigate(`/projects/${project.id}`)}
                            primary={`${project.number} · ${project.name}`}
                            secondary={project.customer_detail?.name}
                            chips={project.is_complete ? <Chip label="Complete" size="small" color="success" sx={{ mr: 0.5 }} /> : null}
                        />
                    )}
                />
            )}

            {tab === 3 && (
                <SearchTab
                    noun="quotes"
                    placeholder="Search quotes by name or number…"
                    searchFn={(term) => QuoteService.search(term)}
                    initialQuery={tab === 3 ? initialQueryRef.current : ""}
                    onQueryChange={handleQueryChange}
                    renderItem={(quote) => (
                        <ResultCard
                            key={quote.id}
                            onClick={() => navigate(`/crm/quotes/${quote.id}`)}
                            primary={`${quote.number} · ${quote.name}`}
                            secondary={quote.customers_detail?.map((c) => c.name).join(", ")}
                            chips={quote.is_complete ? <Chip label="Won" size="small" color="success" sx={{ mr: 0.5 }} /> : null}
                        />
                    )}
                />
            )}
        </Box>
    );
}

export default CRM;
